import {
  Dispatch,
  SetStateAction,
  useState,
  useRef,
  useMemo,
  useEffect,
} from 'react';
import { useSearchTeamMember } from './hooks/useSearchTeamMember';
import { TeamWidgetSearchBox } from './styles/TeamWidgetAutocomplete.styled';
import { handleKeyDownInput } from './utils/handleKeyDown';
import { TeamWidgetSuggestionSection } from './TeamWidgetSuggestionSection';

import { appearance } from 'src/hooks/useAlertMessage';
import { Employee } from 'src/tabs/hooks/useGetAllUsers';

import { ColorCodedEmployee } from './TeamWidgetSection';
import { analyticsEventSend } from 'src/utils/analytics/analyticsEventSend';
import {
  avatarBackgroundColors,
  avatarColors,
} from '../../TeamVacationCircle/styles/TeamVacationCircle.styled';

interface TeamWidgetAutocompleteProps {
  team: Array<Employee>;
  setTeam: Dispatch<SetStateAction<Array<ColorCodedEmployee>>>;

  setShowMessage: (message: string, appearance: appearance) => void;
  users: Array<Employee>;

  groups: { [k in string]: string };
  getGroupUsers: (groupId: string) => Promise<void>;
  groupUsersEmails: Array<string>;
}

export const TeamWidgetAutocomplete = ({
  team,
  setTeam,
  setShowMessage,
  users,
  groups,
  getGroupUsers,
  groupUsersEmails,
}: TeamWidgetAutocompleteProps) => {
  const selectorRef = useRef(null);
  const inputRef = useRef(null);

  const [isSearchingByGroup, setIsSearchingByGroup] = useState(false);
  const [onFocus, setOnFocus] = useState(false);
  const { teamOptions, inputValue, setInputValue, setTeamOptions } =
    useSearchTeamMember(users, team);

  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  const { groupsData } = useMemo(() => {
    if (!inputValue) return { groupsData: [] };

    const filteredGroups = Object.entries(groups).reduce(
      (acc, [id, displayName]) => {
        if (
          displayName.toLowerCase().includes(inputValue.toLocaleLowerCase())
        ) {
          return [
            ...acc,
            {
              [id]: displayName,
            },
          ];
        }
        return [...acc];
      },
      [] as { [k in string]: string }[]
    );

    return {
      groupsData: filteredGroups.length > 0 ? filteredGroups : [],
    };
  }, [inputValue]);

  const isSuggetionsVisible = useMemo(
    () => (groupsData.length > 0 || teamOptions.length > 0) && onFocus,
    [groupsData, teamOptions, onFocus]
  );

  const handleOptionClick = (option: Employee) => {
    setInputValue('');
    setTeamOptions([]);
    if (isSearchingByGroup) {
      setIsSearchingByGroup(false);
    }
    if (!isSearchingByGroup && team.length > 9) {
      setShowMessage(
        'You cannot add more than 10 people to the team.',
        'error'
      );
      return;
    }
    setTeam((oldArray) => {
      const teamArray = [...oldArray, { ...option }];
      return teamArray.map((teamMember, index) => {
        return {
          ...teamMember,
          backgroundPersona: avatarBackgroundColors[index],
          colorPersona: avatarColors[index],
        };
      });
    });
    analyticsEventSend('Added a user to team widget tracker from search box');
  };

  useEffect(() => {
    if (!groupUsersEmails.length) return;

    setTeamOptions([]);
    setInputValue('');
    setIsSearchingByGroup(true);
    if (groupUsersEmails.length > 0) {
      const foundUsers = users.filter((user) =>
        groupUsersEmails.includes(user.cra0e_email)
      );
      if (foundUsers.length === 0) return;

      foundUsers.forEach((user) => {
        setTeam((oldArray) => {
          const teamArray = [...oldArray, { ...user }];
          return teamArray.map((teamMember, index) => {
            return {
              ...teamMember,
              backgroundPersona: avatarBackgroundColors[index],
              colorPersona: avatarColors[index],
            };
          });
        });
      });
    }
  }, [groupUsersEmails]);

  return (
    <>
      <TeamWidgetSearchBox
        id="autocomplete-input"
        placeholder="Search for a person"
        onEscape={() => setTeamOptions([])}
        onClear={() => {
          setInputValue('');
          setTeamOptions([]);
        }}
        onFocus={() => setOnFocus(true)}
        value={inputValue}
        ref={inputRef}
        onKeyDown={(e) => handleKeyDownInput(e, selectorRef)}
        onChange={(_, newValue) => handleInputChange(newValue)}
      />
      <TeamWidgetSuggestionSection
        isSuggetionsVisible={isSuggetionsVisible}
        selectorRef={selectorRef}
        inputRef={inputRef}
        teamOptions={teamOptions}
        setTeam={setTeam}
        setInputValue={setInputValue}
        setTeamOptions={setTeamOptions}
        handleOptionClick={handleOptionClick}
        groups={groupsData}
        getGroupUsers={getGroupUsers}
      />
    </>
  );
};
